import React, { useEffect, useState } from 'react';
import {
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, NavLink, useLocation } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../messages/ThreadQuill.css';

import Loader from '../../shared/displayComponents/loader/Loader';
import { fetchPlayer, deletePlayer, openOrSendFirstMessage, sendPlayerInvite } from '../store/actions';
import {
  addToFavourites,
  deleteFromFavourites,
} from '../../shared/store/authSlice.js';
import {
  addToCompare,
  removeFromCompare,
  selectCompareIds,
  MAX_COMPARE_PLAYERS,
} from '../../shared/store/compareSlice';
import trackEvent from '../../shared/utils/eventTracking';
import { toasterOn } from '../../shared/store/actions';
import notFound from '../../assets/notFound.png';
import { playerPhysicalFeature } from '../createPlayer/selectObjects';
import DeletePlayerModal from '../../shared/displayComponents/deletePlayerModal/DeletePlayerModal';
import AdminChecklist from '../../checklist/AdminChecklist';
import CommunicationLog from '../../checklist/CommunicationLog';
import InternalNotes from '../../checklist/InternalNotes';
import { fetchChecklist } from '../../checklist/store/checklistSlice';

import './PlayerPage.scss';

const replyQuillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const replyQuillFormats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

const isQuillEmpty = val => !val || val.replace(/<[^>]*>/g, '').trim() === '';

const PlayerPage = ({ match, staticPlayer }) => {
  const { player } = useSelector(({ players }) => players);
  const history = useHistory();
  const location = useLocation();
  const viewSource = (location.state && location.state.source) || 'direct';
  const { isLoading } = useSelector(({ shared }) => shared);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { userRole, favouritesPlayers, userEmail } = useSelector(
    ({ authorization }) => authorization,
  );
  const checklistItems = useSelector(({ checklist }) => checklist.items);
  const comparePlayerIds = useSelector(selectCompareIds);
  const playerId = !staticPlayer && match.params.id;
  const [sendingMessage, setSendingMessage] = useState(false);

  const onSubmit = async () => {
    if (isQuillEmpty(contactText) || sendingMessage) return;
    setSendingMessage(true);
    const result = await dispatch(
      openOrSendFirstMessage({ playerId, text: contactText })
    );
    setSendingMessage(false);
    if (result) {
      setShowContact(false);
      setContactText('');
      dispatch(toasterOn({ type: 'success', message: 'Message sent! View it in Messages.' }));
    }
  };

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('task')) return 'checklist';
    if (params.get('tab') === 'checklist') return 'checklist';
    return 'profile';
  });

  // Switch to checklist tab whenever ?task= or ?tab=checklist appears in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('task') || params.get('tab') === 'checklist') {
      setActiveTab('checklist');
    }
  }, [location.search]);
  const [showModal, setShowModal] = useState(false);
  const [showContact, setShowContact] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('message') === '1';
  });
  const [contactText, setContactText] = useState('');

  const dispatch = useDispatch();
  const handleCloseModal = () => setShowModal(false);

  useEffect(() => {
    !staticPlayer && dispatch(fetchPlayer(playerId, viewSource));
  }, [dispatch, playerId, staticPlayer, viewSource]);

  // Fetch checklist data when navigating to the Komunikacja tab (if not already loaded for this player)
  useEffect(() => {
    if (activeTab === 'komunikacja' && (userRole === 'admin' || userRole === 'assistant') && !staticPlayer && playerId) {
      dispatch(fetchChecklist(playerId));
    }
  }, [activeTab, dispatch, playerId, staticPlayer, userRole]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenMessage = params.get('message') === '1';
    if (shouldOpenMessage) {
      setActiveTab('profile');
      setShowContact(true);
    }
  }, [location.search]);

  const {
    _id,
    firstName,
    lastName,
    gender,
    dateOfBirth,
    nationality,
    currentSchool,
    currentClub,
    graduationYear,
    highSchoolGpa,
    satScore,
    toeflScore,
    height,
    weight,
    position,
    physicals,
    englishSkills,
    levelOfPlay,
    analysis,
    image,
    youtubeUrl,
    sport,
    accomplishments,
    intendedMajor,
    additionalComments,
    duolingoScore,
    attackReach,
    budget,
    transfer,
    inviteStatus,
  } = !staticPlayer ? player : staticPlayer;

  const deletePlayerHandler = () => {
    dispatch(deletePlayer(_id, history));
  };

  const changeFavouriteStatus = data => {
    favouritesPlayers.includes(data)
      ? dispatch(deleteFromFavourites(userEmail, data))
      : dispatch(addToFavourites(userEmail, data));
  };

  const checkIfPhotoExists = () => {
    if (!staticPlayer) {
      if (image) {
        const buffer = image.data;
        const b64 = Buffer.from(buffer).toString('base64');
        return `data:image/jpeg;base64,${b64}`;
      }
      return notFound;
    }
    return staticPlayer.image;
  };

  const convertDate = () => {
    if (!dateOfBirth) return '-';
    const [year, month, day] = dateOfBirth.split('-');
    return [month, day, year].join('-');
  };

  const isFavourite = (favouritesPlayers || []).includes(_id);
  const isLoggedIn = !!userEmail;
  const isInCompare = comparePlayerIds.includes(_id);

  const handleCompareToggle = () => {
    if (isInCompare) {
      dispatch(removeFromCompare(_id));
      trackEvent('compare_remove_player', { playerId: _id, metadata: { context: 'profile' } });
    } else {
      if (comparePlayerIds.length >= MAX_COMPARE_PLAYERS) {
        dispatch(toasterOn({ message: `You can compare up to ${MAX_COMPARE_PLAYERS} players`, type: 'failed' }));
        return;
      }
      dispatch(addToCompare({ _id, firstName, lastName }));
      trackEvent('compare_add_player', { playerId: _id, metadata: { context: 'profile' } });
    }
  };

  // For static player (Sample Profile), never show loader — there's no fetch to complete.
  // For normal pages, only block render while the player data itself hasn't loaded yet.
  if (staticPlayer ? false : isLoading && !_id) {
    return <Loader />;
  }

  return (
    <div className="detail">
      <DeletePlayerModal
        handleCloseModal={handleCloseModal}
        showModal={showModal}
        deletePlayerHandler={deletePlayerHandler}
      />

      {/* Header */}
      <div className="detail__header">
        {!staticPlayer && userRole !== 'assistant' && (
          <button
            type="button"
            className="detail__back"
            onClick={() => history.push('/available-players')}
          >
            <i className="fas fa-arrow-left" /> Back to Players
          </button>
        )}
        {!staticPlayer && userRole === 'assistant' && (
          <button
            type="button"
            className="detail__back"
            onClick={() => history.push('/admin/my-players')}
          >
            <i className="fas fa-arrow-left" /> Moi zawodnicy
          </button>
        )}
        {staticPlayer && (
          <div className="detail__sample-badge">
            <i className="fas fa-star" /> Sample Player Profile
          </div>
        )}

        <div className="detail__actions">
          {isLoggedIn && !staticPlayer && userRole !== 'assistant' && (
            <OverlayTrigger
              overlay={
                <Tooltip>
                  {!isFavourite ? 'Add to Favorites' : 'Remove from Favorites'}
                </Tooltip>
              }
            >
              <button
                type="button"
                className={`detail__action-btn ${isFavourite ? 'detail__action-btn--active' : ''}`}
                onClick={() => changeFavouriteStatus(_id)}
              >
                <i className={isFavourite ? 'fas fa-heart' : 'far fa-heart'} />
              </button>
            </OverlayTrigger>
          )}

          {isLoggedIn && !staticPlayer && userRole !== 'assistant' && (
            <OverlayTrigger
              overlay={
                <Tooltip>
                  {isInCompare ? 'Remove from compare' : 'Add to compare'}
                </Tooltip>
              }
            >
              <button
                type="button"
                className={`detail__action-btn ${isInCompare ? 'detail__action-btn--compare-active' : ''}`}
                onClick={handleCompareToggle}
              >
                <i className={isInCompare ? 'fas fa-check-square' : 'far fa-clone'} />
              </button>
            </OverlayTrigger>
          )}

          {userRole === 'admin' && !staticPlayer && (
            <>
              <OverlayTrigger overlay={<Tooltip>Send Player Invite</Tooltip>}>
                <button
                  type="button"
                  className="detail__action-btn"
                  onClick={() => dispatch(sendPlayerInvite(_id))}
                >
                  <i className="fas fa-envelope" />
                </button>
              </OverlayTrigger>
              {inviteStatus === 'invited' && (
                <OverlayTrigger overlay={<Tooltip>Invited — awaiting activation</Tooltip>}>
                  <span className="detail__invite-badge detail__invite-badge--invited">Invited</span>
                </OverlayTrigger>
              )}
              {inviteStatus === 'active' && (
                <OverlayTrigger overlay={<Tooltip>Account Active</Tooltip>}>
                  <span className="detail__invite-badge detail__invite-badge--active">Active</span>
                </OverlayTrigger>
              )}
              <NavLink to={`/edit/${_id}`} className="detail__action-btn">
                <i className="fas fa-edit" />
              </NavLink>
              <button
                type="button"
                className="detail__action-btn detail__action-btn--delete"
                onClick={() => setShowModal(true)}
              >
                <i className="fas fa-trash-alt" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs — admin and assistant */}
      {(userRole === 'admin' || userRole === 'assistant') && !staticPlayer && _id && (
        <div className="detail__tabs">
          <button
            type="button"
            className={`detail__tab${activeTab === 'profile' ? ' detail__tab--active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <i className="fas fa-user" /> Profile
          </button>
          <button
            type="button"
            className={`detail__tab${activeTab === 'checklist' ? ' detail__tab--active' : ''}`}
            onClick={() => setActiveTab('checklist')}
          >
            <i className="fas fa-tasks" /> Checklist
          </button>
          {(userRole === 'admin' || userRole === 'assistant') && (
            <button
              type="button"
              className={`detail__tab${activeTab === 'komunikacja' ? ' detail__tab--active' : ''}`}
              onClick={() => setActiveTab('komunikacja')}
            >
              <i className="fas fa-comments" /> Komunikacja
            </button>
          )}
          {(userRole === 'admin' || userRole === 'assistant') && (
            <button
              type="button"
              className={`detail__tab${activeTab === 'notatki' ? ' detail__tab--active' : ''}`}
              onClick={() => setActiveTab('notatki')}
            >
              <i className="fas fa-sticky-note" /> Notatki
            </button>
          )}
        </div>
      )}

      {/* Checklist tab */}
      {(userRole === 'admin' || userRole === 'assistant') &&
        !staticPlayer &&
        _id &&
        activeTab === 'checklist' && <AdminChecklist playerId={_id} />}

      {/* Komunikacja tab */}
      {(userRole === 'admin' || userRole === 'assistant') && !staticPlayer && _id && activeTab === 'komunikacja' && (
        <div style={{ marginTop: '32px', borderTop: '2px solid #e2e8f0', paddingTop: '24px' }}>
          <h3 style={{ color: '#1e293b', fontWeight: '700', marginBottom: '16px', fontSize: '18px' }}>
            <i className="fas fa-comments" style={{ marginRight: '8px', color: '#2e496b' }} />
            Komunikacja
          </h3>
          <CommunicationLog
            items={checklistItems}
            onTaskClick={(taskId) => {
              setActiveTab('checklist');
              history.replace({ ...location, search: `?task=${taskId}` });
            }}
          />
        </div>
      )}

      {/* Notatki tab */}
      {(userRole === 'admin' || userRole === 'assistant') && !staticPlayer && _id && activeTab === 'notatki' && (
        <div style={{ marginTop: '32px', borderTop: '2px solid #e2e8f0', paddingTop: '24px' }}>
          <h3 style={{ color: '#1e293b', fontWeight: '700', marginBottom: '16px', fontSize: '18px' }}>
            <i className="fas fa-sticky-note" style={{ marginRight: '8px', color: '#2e496b' }} />
            Notatki wewnętrzne
          </h3>
          <InternalNotes playerId={_id} />
        </div>
      )}

      {/* Main Content */}
      <div className="detail__main" style={(userRole === 'admin' || userRole === 'assistant') && !staticPlayer && activeTab !== 'profile' ? { display: 'none' } : {}}>
        {/* Left Column - Photo & Contact */}
        <div className="detail__left">
          <div className="detail__photo-wrapper">
            <img src={checkIfPhotoExists()} alt={`${firstName} ${lastName}`} className="detail__photo" />
            {transfer && <span className="detail__badge">Transfer</span>}
          </div>

          <div className="detail__name-section">
            <h1 className="detail__name">{firstName} {lastName}</h1>
            <p className="detail__sport">
              <i className="fas fa-volleyball-ball" /> {sport}
            </p>
          </div>

          {isLoggedIn && !staticPlayer && userRole !== 'assistant' && (
            <button
              type="button"
              className="detail__contact-btn"
              onClick={() => setShowContact(!showContact)}
            >
              <i className="fas fa-envelope" />
              {' Contact this Player'}
            </button>
          )}

          {showContact && !staticPlayer && isLoggedIn && userRole !== 'assistant' && (
            <div className="detail__contact-form">
              <div className="thread-quill thread-quill--contact" style={{ marginBottom: '8px' }}>
                <ReactQuill
                  value={contactText}
                  onChange={setContactText}
                  modules={replyQuillModules}
                  formats={replyQuillFormats}
                  theme="snow"
                  placeholder="Type your message here..."
                />
              </div>
              <button
                type="button"
                className="detail__send-btn"
                disabled={isQuillEmpty(contactText) || sendingMessage}
                onClick={onSubmit}
              >
                {sendingMessage
                  ? <><i className="fas fa-spinner fa-spin" /> Sending...</>
                  : <><i className="fas fa-paper-plane" /> Send Message</>}
              </button>
            </div>
          )}
        </div>

        {/* Right Column - Info */}
        <div className="detail__right">
          {/* Basic Info */}
          <div className="detail__section">
            <h3 className="detail__section-title">
              <i className="fas fa-user" /> Basic Information
            </h3>
            <div className="detail__grid">
              <div className="detail__item">
                <span className="detail__label">Date of Birth</span>
                <span className="detail__value">{convertDate()}</span>
              </div>
              <div className="detail__item">
                <span className="detail__label">Nationality</span>
                <span className="detail__value">{nationality || '-'}</span>
              </div>
              <div className="detail__item">
                <span className="detail__label">Gender</span>
                <span className="detail__value">{gender || '-'}</span>
              </div>
              <div className="detail__item">
                <span className="detail__label">Height</span>
                <span className="detail__value">{height || '-'}</span>
              </div>
              <div className="detail__item">
                <span className="detail__label">Weight</span>
                <span className="detail__value">{weight || '-'}</span>
              </div>
              {attackReach && (
                <div className="detail__item">
                  <span className="detail__label">Attack Reach</span>
                  <span className="detail__value">{attackReach}</span>
                </div>
              )}
              {position && position.length > 0 && (
                <div className="detail__item detail__item--full">
                  <span className="detail__label">{sport === 'Track & Field' ? 'Event' : 'Position'}</span>
                  <span className="detail__value">{position.join(', ')}</span>
                </div>
              )}
            </div>
          </div>

          {/* School & Club */}
          <div className="detail__section">
            <h3 className="detail__section-title">
              <i className="fas fa-school" /> {transfer ? 'University' : 'High School'} & Club
            </h3>
            <div className="detail__grid">
              <div className="detail__item">
                <span className="detail__label">{transfer ? 'University Name' : 'High School Name'}</span>
                <span className="detail__value">{currentSchool || '-'}</span>
              </div>
              <div className="detail__item">
                <span className="detail__label">Current Club</span>
                <span className="detail__value">{currentClub || '-'}</span>
              </div>
              <div className="detail__item">
                <span className="detail__label">Available From</span>
                <span className="detail__value">{graduationYear || '-'}</span>
              </div>
              <div className="detail__item">
                <span className="detail__label">{transfer ? 'University GPA' : 'High School GPA'}</span>
                <span className="detail__value">{highSchoolGpa || '-'}</span>
              </div>
              {satScore && (
                <div className="detail__item">
                  <span className="detail__label">SAT Score</span>
                  <span className="detail__value">{satScore}</span>
                </div>
              )}
              {toeflScore && (
                <div className="detail__item">
                  <span className="detail__label">TOEFL Score</span>
                  <span className="detail__value">{toeflScore}</span>
                </div>
              )}
              {duolingoScore && (
                <div className="detail__item">
                  <span className="detail__label">Duolingo Score</span>
                  <span className="detail__value">{duolingoScore}</span>
                </div>
              )}
              {intendedMajor && (
                <div className="detail__item">
                  <span className="detail__label">Intended Major</span>
                  <span className="detail__value">{intendedMajor}</span>
                </div>
              )}
              {budget && (
                <div className="detail__item">
                  <span className="detail__label">Budget</span>
                  <span className="detail__value">{budget}</span>
                </div>
              )}
            </div>
            {accomplishments && accomplishments.length > 0 && (
              <div className="detail__list-section">
                <span className="detail__label">Accomplishments</span>
                <ul className="detail__list">
                  {accomplishments.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Player Evaluation */}
          <div className="detail__section">
            <h3 className="detail__section-title">
              <i className="fas fa-star" /> Player Evaluation
            </h3>
            <div className="detail__grid">
              <div className="detail__item">
                <span className="detail__label">English Proficiency</span>
                <span className="detail__value">{englishSkills || '-'}</span>
              </div>
              <div className="detail__item">
                <span className="detail__label">Expected Level of Play</span>
                <span className="detail__value">{levelOfPlay?.length ? levelOfPlay.join(', ') : '-'}</span>
              </div>
            </div>

            <div className="detail__two-columns">
              {/* Left column */}
              <div className="detail__column">
                {physicals && physicals.length > 0 && (
                  <div className="detail__list-section">
                    <span className="detail__label">Physical Attributes</span>
                    <ul className="detail__list">
                      {physicals.map((phys, i) => {
                        const feature = playerPhysicalFeature.find(f => f.key === phys);
                        return (
                          <li key={i}>
                            {feature && <strong>{feature.section}:</strong>} {phys}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              {/* Right column */}
              <div className="detail__column">
                {analysis && analysis.length > 0 && (
                  <div className="detail__list-section">
                    <span className="detail__label">In-Person Analysis</span>
                    <ul className="detail__list">
                      {analysis.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {additionalComments && additionalComments.replace(/<[^>]*>/g, '').trim().length > 0 && (
              <div className="detail__comments">
                <span className="detail__label">Additional Comments</span>
                <div 
                  className="detail__comments-text"
                  dangerouslySetInnerHTML={{ __html: additionalComments }}
                />
              </div>
            )}
          </div>

          {/* Videos */}
          {youtubeUrl && youtubeUrl.length > 0 && (
            <div className="detail__section detail__section--videos">
              <h3 className="detail__section-title">
                <i className="fab fa-youtube" /> Player Videos
                <span className="detail__video-count">{youtubeUrl.length} video{youtubeUrl.length > 1 ? 's' : ''}</span>
              </h3>
              <div className="detail__videos">
                {youtubeUrl.map((videoUrl, idx) => {
                  // Add parameters to clean up YouTube player
                  const cleanUrl = videoUrl.includes('?')
                    ? `${videoUrl}&modestbranding=1&rel=0&showinfo=0`
                    : `${videoUrl}?modestbranding=1&rel=0&showinfo=0`;
                  return (
                    <div key={idx} className="detail__video-wrapper">
                      <iframe
                        title={`player_video_${idx}`}
                        className="detail__video"
                        src={cleanUrl}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default PlayerPage;
